import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { patientsApi, doctorsApi, companiesApi, recordsApi } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, ArrowLeft, Search, History, Clock, User, Hash, RefreshCw, Plus, ChevronsUpDown, Check, Stethoscope } from "lucide-react";
import { debounce, cn } from "@/lib/utils";

export default function NewPatient() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refreshKey = searchParams.get('_r');
  
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [defaultDoctorId, setDefaultDoctorId] = useState("");
  const [defaultCompanyId, setDefaultCompanyId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedExistingPatient, setSelectedExistingPatient] = useState(null);
  
  // Doctor search states
  const [doctorSearchOpen, setDoctorSearchOpen] = useState(false);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
  
  // New doctor modal states
  const [newDoctorDialogOpen, setNewDoctorDialogOpen] = useState(false);
  const [savingDoctor, setSavingDoctor] = useState(false);
  const [newDoctorFormData, setNewDoctorFormData] = useState({
    name: "",
    specialty: "",
    license_number: "",
    phone: "",
    email: "",
    active: true,
  });
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    birth_date: "",
    gender: "male",
    age: "",
    phone: "",
    email: "",
    address: "",
    doctor_id: "",
    company_id: "",
    expediente: "", // Will be set if re-entering existing patient
  });

  // Cargar datos iniciales (doctores y compañías)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [doctorsRes, companiesRes] = await Promise.all([
          doctorsApi.getAll(),
          companiesApi.getAll(),
        ]);
        
        const doctorsList = doctorsRes.data;
        const companiesList = companiesRes.data;
        
        // Find default doctor and company
        const defaultDoctor = doctorsList.find(
          d => d.name?.toUpperCase() === "A QUIEN CORRESPONDA"
        );
        const particularCompany = companiesList.find(
          c => c.name?.toUpperCase() === "PARTICULAR"
        );
        
        const doctorId = defaultDoctor?.id || "";
        const companyId = particularCompany?.id || "";
        
        // Set states
        setDoctors(doctorsList);
        setCompanies(companiesList);
        setDefaultDoctorId(doctorId);
        setDefaultCompanyId(companyId);
        
        // Set form data with defaults
        setFormData(prev => ({
          ...prev,
          doctor_id: doctorId,
          company_id: companyId
        }));
        
        setDataLoaded(true);
        
      } catch (error) {
        console.error("Error loading data:", error);
        setDataLoaded(true);
      }
    };
    fetchData();
  }, []); // Solo ejecutar una vez al montar el componente
  
  // Recargar datos cuando cambie refreshKey (navegación)
  useEffect(() => {
    if (refreshKey) {
      // Solo recargar listas, no resetear valores por defecto
      const reloadLists = async () => {
        try {
          const [doctorsRes, companiesRes] = await Promise.all([
            doctorsApi.getAll(),
            companiesApi.getAll(),
          ]);
          setDoctors(doctorsRes.data);
          setCompanies(companiesRes.data);
        } catch (error) {
          console.error("Error reloading data:", error);
        }
      };
      reloadLists();
    }
  }, [refreshKey]);

  // Filter doctors by search term (partial match)
  const filteredDoctors = doctors.filter((doctor) =>
    doctor.name?.toLowerCase().includes(doctorSearchTerm.toLowerCase())
  );

  // Get selected doctor name for display
  const getSelectedDoctorName = () => {
    const selectedDoctor = doctors.find(d => d.id === formData.doctor_id);
    return selectedDoctor?.name || "Seleccionar médico";
  };

  // Handle new doctor form submission
  const handleCreateDoctor = async (e) => {
    e.preventDefault();
    if (!newDoctorFormData.name.trim()) {
      toast.error("El nombre del doctor es requerido");
      return;
    }
    
    setSavingDoctor(true);
    try {
      const response = await doctorsApi.create(newDoctorFormData);
      const newDoctor = response.data;
      
      // Add to local list and select it
      setDoctors(prev => [...prev, newDoctor]);
      setFormData(prev => ({ ...prev, doctor_id: newDoctor.id }));
      
      // Reset form and close dialog
      setNewDoctorFormData({
        name: "",
        specialty: "",
        license_number: "",
        phone: "",
        email: "",
        active: true,
      });
      setNewDoctorDialogOpen(false);
      toast.success(`Doctor "${newDoctor.name}" creado y seleccionado`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear doctor");
    } finally {
      setSavingDoctor(false);
    }
  };

  // Debounced search function - uses records API for expediente grouping
  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (!term || term.length < 1) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      setSearching(true);
      try {
        const response = await recordsApi.searchPatients(term);
        setSearchResults(response.data);
        setShowSearchResults(true);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setSearching(false);
      }
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Calculate birth date from age (use current date as reference)
  const calculateBirthDateFromAge = (age) => {
    if (!age || isNaN(age)) return "";
    const today = new Date();
    const birthYear = today.getFullYear() - parseInt(age);
    const birthMonth = String(today.getMonth() + 1).padStart(2, '0');
    const birthDay = String(today.getDate()).padStart(2, '0');
    return `${birthYear}-${birthMonth}-${birthDay}`;
  };

  // Calculate age from birth date
  const calculateAgeFromBirthDate = (birthDate) => {
    if (!birthDate) return "";
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age.toString();
  };

  // Handle age change - calculate birth date
  const handleAgeChange = (e) => {
    const age = e.target.value;
    setFormData((prev) => ({
      ...prev,
      age,
      birth_date: age ? calculateBirthDateFromAge(age) : prev.birth_date,
    }));
  };

  // Handle birth date change - calculate age
  const handleBirthDateChange = (e) => {
    const birthDate = e.target.value;
    setFormData((prev) => ({
      ...prev,
      birth_date: birthDate,
      age: birthDate ? calculateAgeFromBirthDate(birthDate) : prev.age,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Select existing patient to re-enter with same expediente but new folio
  const selectExistingPatient = (patient) => {
    setShowSearchResults(false);
    setSearchTerm("");
    setSelectedExistingPatient(patient);
    
    // Pre-fill form with patient data but will generate new folio
    setFormData({
      first_name: patient.first_name,
      last_name: patient.last_name,
      birth_date: patient.birth_date || "",
      gender: patient.gender || "male",
      age: "",
      phone: "",
      email: "",
      address: "",
      doctor_id: defaultDoctorId,
      company_id: defaultCompanyId,
      expediente: patient.expediente, // Keep same expediente
    });
    
    // Calculate age from birth date
    if (patient.birth_date) {
      const age = calculateAgeFromBirthDate(patient.birth_date);
      setFormData((prev) => ({ ...prev, age }));
    }
    
    toast.info(`Paciente seleccionado: ${patient.name}. Se generará un nuevo folio.`);
  };

  // Clear selected patient and reset form
  const clearSelectedPatient = () => {
    setSelectedExistingPatient(null);
    setFormData({
      first_name: "",
      last_name: "",
      birth_date: "",
      gender: "male",
      age: "",
      phone: "",
      email: "",
      address: "",
      doctor_id: defaultDoctorId,
      company_id: defaultCompanyId,
      expediente: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
      };
      if (!dataToSend.doctor_id) delete dataToSend.doctor_id;
      if (!dataToSend.company_id) delete dataToSend.company_id;
      if (!dataToSend.expediente) delete dataToSend.expediente;

      const response = await patientsApi.create(dataToSend);
      
      const message = selectedExistingPatient 
        ? `Paciente reingresado. Expediente: ${response.data.expediente}`
        : `Paciente registrado. Expediente: ${response.data.expediente}`;
      
      toast.success(message);
      
      // Pasar el doctor_id seleccionado en el formulario como query parameter
      const doctorParam = formData.doctor_id ? `?doctor_id=${formData.doctor_id}` : '';
      navigate(`/reception/create-order/${response.data.id}${doctorParam}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar paciente");
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const currentDate = now.toLocaleDateString("es-MX", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const currentTime = now.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="space-y-6" data-testid="new-patient-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            Ingresar Nuevo Paciente
          </h1>
          <p className="text-muted-foreground">
            Complete los datos del paciente o busque uno existente
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted px-4 py-2 rounded-lg">
          <Clock className="h-4 w-4" />
          <span>{currentDate} - {currentTime}</span>
        </div>
      </div>

      {/* Search Existing Patient - Real-time */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5" />
            Buscar Paciente Existente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
            <Input
              placeholder="Escriba nombre o número de expediente..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
              data-testid="search-patient-input"
            />
            {searching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              </div>
            )}
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border shadow-lg max-h-64 overflow-y-auto">
                {searchResults.map((patient, index) => (
                  <button
                    key={patient.expediente}
                    className={`w-full px-4 py-3 text-left flex items-center gap-3 border-b last:border-b-0 ${
                      index === 0 ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                    onClick={() => selectExistingPatient(patient)}
                    data-testid={`patient-result-${patient.expediente}`}
                  >
                    <div className={`p-2 rounded-full ${index === 0 ? 'bg-primary-foreground/20' : 'bg-secondary'}`}>
                      <User className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{patient.name}</p>
                        <Badge variant={index === 0 ? "secondary" : "outline"} className="font-mono text-xs">
                          <Hash className="h-3 w-3 mr-1" />
                          {patient.expediente}
                        </Badge>
                      </div>
                      <p className={`text-sm ${index === 0 ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                        {patient.visits?.length || 0} ingreso(s) previo(s)
                      </p>
                    </div>
                    <RefreshCw className={`h-4 w-4 ${index === 0 ? 'text-primary-foreground/70' : 'text-muted-foreground'}`} />
                  </button>
                ))}
              </div>
            )}
            
            {showSearchResults && searchResults.length === 0 && searchTerm.length >= 1 && !searching && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border shadow-lg p-4 text-center text-muted-foreground">
                No se encontraron pacientes
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Al seleccionar un paciente existente, se mantendrá su expediente y se generará un nuevo folio para este ingreso
          </p>
        </CardContent>
      </Card>

      {/* Selected Patient Banner */}
      {selectedExistingPatient && (
        <Card className="bg-primary/5 border-primary">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-full">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{selectedExistingPatient.name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="font-mono">Expediente: {selectedExistingPatient.expediente}</span>
                    <span>•</span>
                    <span>Se generará nuevo folio</span>
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={clearSelectedPatient}>
                Cambiar paciente
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* New Patient Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserPlus className="h-5 w-5" />
            {selectedExistingPatient ? "Confirmar Datos del Paciente" : "Datos del Nuevo Paciente"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="first_name">Nombre *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Juan"
                  required
                  data-testid="patient-first-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Apellidos *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Pérez García"
                  required
                  data-testid="patient-last-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="age">Edad *</Label>
                <Input
                  id="age"
                  name="age"
                  type="number"
                  min="0"
                  max="150"
                  value={formData.age}
                  onChange={handleAgeChange}
                  placeholder="Ingrese edad"
                  data-testid="patient-age"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender">Género *</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, gender: value }))
                  }
                >
                  <SelectTrigger data-testid="patient-gender">
                    <SelectValue placeholder="Seleccionar género" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Masculino</SelectItem>
                    <SelectItem value="female">Femenino</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">Fecha de Nacimiento</Label>
                <Input
                  id="birth_date"
                  name="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={handleBirthDateChange}
                  data-testid="patient-birth-date"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="555-123-4567"
                  data-testid="patient-phone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="paciente@email.com"
                  data-testid="patient-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="doctor_id">Médico que envía</Label>
                {dataLoaded ? (
                  <div className="flex gap-2">
                    <Popover open={doctorSearchOpen} onOpenChange={setDoctorSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={doctorSearchOpen}
                          className="flex-1 justify-between font-normal"
                          data-testid="patient-doctor"
                        >
                          <span className="truncate">{getSelectedDoctorName()}</span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Buscar médico..."
                            value={doctorSearchTerm}
                            onValueChange={setDoctorSearchTerm}
                            data-testid="doctor-search-input"
                          />
                          <CommandList>
                            <CommandEmpty>No se encontró el médico</CommandEmpty>
                            <CommandGroup className="max-h-60 overflow-y-auto">
                              {filteredDoctors.map((doctor) => (
                                <CommandItem
                                  key={doctor.id}
                                  value={doctor.id}
                                  onSelect={() => {
                                    setFormData(prev => ({ ...prev, doctor_id: doctor.id }));
                                    setDoctorSearchOpen(false);
                                    setDoctorSearchTerm("");
                                  }}
                                  className={cn(
                                    "cursor-pointer",
                                    formData.doctor_id === doctor.id && "bg-primary text-primary-foreground"
                                  )}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.doctor_id === doctor.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {doctor.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setNewDoctorDialogOpen(true)}
                      title="Agregar nuevo médico"
                      data-testid="add-doctor-inline-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="h-10 bg-gray-100 rounded-md animate-pulse" />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_id">Compañía</Label>
                {dataLoaded ? (
                  <Select
                    value={formData.company_id}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, company_id: value }))
                    }
                  >
                    <SelectTrigger data-testid="patient-company">
                      <SelectValue placeholder="Seleccionar compañía" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="h-10 bg-gray-100 rounded-md animate-pulse" />
                )}
              </div>

              <div className="space-y-2 md:col-span-3">
                <Label htmlFor="address">Observaciones</Label>
                <Textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Información adicional del paciente (alergias, medicamentos, indicaciones especiales...)"
                  rows={2}
                  data-testid="patient-observations"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-patient-btn">
                {loading 
                  ? "Registrando..." 
                  : selectedExistingPatient 
                    ? "Reingresar Paciente" 
                    : "Registrar y Continuar"
                }
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Modal para crear nuevo doctor */}
      <Dialog open={newDoctorDialogOpen} onOpenChange={setNewDoctorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5" />
              Nuevo Médico
            </DialogTitle>
            <DialogDescription>
              Complete los datos del médico. El nuevo médico quedará vinculado al catálogo de doctores.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateDoctor} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={newDoctorFormData.name}
                onChange={(e) =>
                  setNewDoctorFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Nombre completo del médico"
                required
                data-testid="new-doctor-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Especialidad</Label>
                <Input
                  value={newDoctorFormData.specialty}
                  onChange={(e) =>
                    setNewDoctorFormData((prev) => ({ ...prev, specialty: e.target.value }))
                  }
                  placeholder="Ej: Cardiología"
                />
              </div>
              <div className="space-y-2">
                <Label>Cédula Profesional</Label>
                <Input
                  value={newDoctorFormData.license_number}
                  onChange={(e) =>
                    setNewDoctorFormData((prev) => ({
                      ...prev,
                      license_number: e.target.value,
                    }))
                  }
                  placeholder="Número de cédula"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={newDoctorFormData.phone}
                  onChange={(e) =>
                    setNewDoctorFormData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  placeholder="555-123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newDoctorFormData.email}
                  onChange={(e) =>
                    setNewDoctorFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="doctor@email.com"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={newDoctorFormData.active}
                onCheckedChange={(checked) =>
                  setNewDoctorFormData((prev) => ({ ...prev, active: checked }))
                }
              />
              <Label>Activo</Label>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setNewDoctorDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={savingDoctor} data-testid="save-new-doctor-btn">
                {savingDoctor ? "Guardando..." : "Crear Médico"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
